import React, { memo, useState, useMemo, useEffect, ReactNode, Fragment } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { classnames } from '@core/utils/css';
import { useRouteParam, useHistory } from '@core/router';
import { R } from '@core/utils/r';
import { PermissionProvider } from '@core/components/PermissionProvider';
import { GCModule } from '@business/constants/permissions';

export interface TabHeaderItem {
  title: string;
  leftIcon?: string;
  rightIcon?: string;
  leftHTML?: React.ReactNode;
  rightHTML?: React.ReactNode;
  route?: string;
  visible?: boolean;
}

export interface TabsHeaderProps {
  tabs: TabHeaderItem[];
  initialIndex?: number;
  pills?: boolean;
  simple?: boolean;
  routeParam?: string;
  route?(tab: string): string;
  isModal?: boolean;
  onChange?(index: number): void;
  index?: number;
  children?: ReactNode;
  right?: ReactNode;
  extraClasses?: string;
  borderBottom?: boolean;
  hasPermission?: boolean;
}

export enum SearchPermissionType {
  Organization = 'organizations',
  Deal = 'deals',
  Project = 'project-board',
  Subscriptions = 'marketing',
  Contact = 'contacts',
  Order = 'orders',
  'Work Order' = 'timeMaterial',
  Library = 'library',
  Glossary = 'glossary',
}

function getPermissionValueByTitle(title: keyof typeof SearchPermissionType): string {
  return SearchPermissionType[title];
}

export const TabsHeader = memo((props: TabsHeaderProps) => {
  const { pills = false, simple = false, isModal = false, onChange, route, routeParam, borderBottom = false, hasPermission = false } = props;
  const tabs = useMemo(() => props.tabs.filter(t => t.visible !== false), [props.tabs]);

  let routeTabParam = '';
  if (!isModal && props.routeParam) {
    routeTabParam = useRouteParam(props.routeParam || 'randomRouteParam', true);
  }
  const history = useHistory();
  const [lastRoute, setLastRoute] = useState('');

  const initialIndex = useMemo(() => {
    if (props.initialIndex) {
      return props.initialIndex;
    } else if (props.routeParam && routeTabParam) {
      return tabs.findIndex(t => t.route === routeTabParam) || 0;
    }
    return 0;
  }, [props.initialIndex, props.routeParam, routeTabParam, tabs]);

  const [activeTabIndex, setActiveTabIndex] = useState<number>(initialIndex);

  useEffect(() => {
    if (!R.isUndefined(props.index)) {
      setActiveTabIndex(props.index);
    }
  }, [props.index]);

  useEffect(() => {
    const tab = tabs[activeTabIndex];
    if (route && tab && tab.route && routeParam) {
      const url = route(tab.route);
      if (url !== lastRoute) {
        setLastRoute(url);
        history.replace(url);
      }
    }
  }, [activeTabIndex, history, lastRoute, route, routeParam, tabs]);

  useEffect(() => {
    onChange && onChange(activeTabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex]);

  const navClasses = useMemo(() => {
    const items = ['gc-tabs-nav'];
    if (pills) {
      items.push('navtab-bg');
    } else {
      if (!simple) {
        items.push('nav-tabs-custom');
      }
    }
    return items;
  }, [pills, simple]);

  const NavTabs = useMemo(() => {
    return ({ index, tab }: { index: number; tab: TabHeaderItem }) => (
      <NavItem key={`tab-${index}`}>
        <NavLink
          style={{ cursor: 'pointer' }}
          className={classnames({
            active: activeTabIndex === index,
            'border-bottom border-primary': activeTabIndex === index && borderBottom,
          })}
          onClick={() => {
            const { index: propIndex, onChange } = props;
            if (propIndex !== undefined && onChange) {
              onChange(index);
            } else {
              setActiveTabIndex(index);
            }
          }}
        >
          {tab.leftIcon && <i className={tab.leftIcon}></i>}
          {tab.leftHTML}
          <span className={tab.leftIcon || tab.rightIcon ? 'd-none d-sm-block' : ''}>{tab.title}</span>
          {tab.rightHTML}
          {tab.rightIcon && <i className={tab.rightIcon}></i>}
        </NavLink>
      </NavItem>
    );
  }, [activeTabIndex, borderBottom, props]);
  const navBar = (
    <Fragment>
      <Nav pills={pills} tabs={!pills} className={`${classnames(navClasses)} ${props.extraClasses}`}>
        {tabs.map((tab, index) => (
          <>
            {hasPermission ? (
              <>
                {tab.title === 'All' ? (
                  <NavTabs index={index} tab={tab} />
                ) : (
                  <PermissionProvider module={getPermissionValueByTitle(tab.title as keyof typeof SearchPermissionType) as GCModule}>
                    <NavTabs index={index} tab={tab} />
                  </PermissionProvider>
                )}
              </>
            ) : (
              <NavTabs index={index} tab={tab} />
            )}
          </>
        ))}
      </Nav>
      <div className="d-flex align-items-center justify-content-end" style={{ flex: 1 }}>
        {props.right}
      </div>
    </Fragment>
  );

  return (
    <Fragment>
      {props.right ? <div className="d-flex gc-tabs-row">{navBar}</div> : navBar}
      {!!props.children && (
        <TabContent>
          <TabPane className="py-3">{props.children}</TabPane>
        </TabContent>
      )}
    </Fragment>
  );
});
